import React, { PureComponent } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { object, number, bool, string, arrayOf, objectOf } from 'prop-types';
import { choiceBadgeEnrichment } from '../../../utils/experiments/choiceBadgeEnrichment';
import { LOGS_KEY } from '../../../utils/constants';
import { logger } from '../../../shared/logger';
import { fetchComponentContent } from './api/lazyComponentsHandlers';

const lazyComponentStub = (name) => ({
    items: {
        gigs: [],
    },
    contextEnrichment: {
        context: {
            sourceComponent: name,
            pageCtxId: '',
        },
        isTouch: false,
    },
});

export const withSelfFetching = (WrappedComponent) => {
    class SelfFetchingComponent extends PureComponent {
        constructor(props) {
            super(props);

            const { isLoading, isLazy, component } = this.props;

            this.state = {
                isError: false,
                isLoading: isLoading || isLazy,
                componentData: !isLazy ? component : lazyComponentStub(component.name),
            };
            this.fetchComponentData = this.fetchComponentData.bind(this);
        }

        componentDidMount() {
            this.fetchComponentData();
        }

        componentDidUpdate(prevProps) {
            if (
                !isEqual(prevProps.component, this.props.component) ||
                prevProps.perseusName !== this.props.perseusName ||
                prevProps.appName !== this.props.appName ||
                prevProps.sectionName !== this.props.sectionName
            ) {
                this.fetchComponentData();
            }
        }

        async fetchComponentData() {
            try {
                const {
                    component,
                    sectionName,
                    isLazy,
                    recentlyViewedChoiceGigs,
                    abTests,
                    choiceAlgos,
                    appName,
                    perseusName,
                } = this.props;
                let componentData = !isLazy
                    ? component
                    : await fetchComponentContent({ perseusName, appName, sectionName, componentName: component.name });
                const { items } = componentData;

                if (isEmpty(items)) {
                    const error = new Error('Component does not contain any renderable items');

                    error.component_data = componentData;
                    throw error;
                }

                if (choiceAlgos && !isEmpty(items.gigs)) {
                    componentData = await choiceBadgeEnrichment({
                        abTests,
                        recentlyViewedChoiceGigs,
                        choiceAlgos,
                        componentData,
                    });
                }

                this.setState({ componentData, isLoading: false, isError: false });
            } catch (error) {
                this.setState({
                    isLoading: false,
                    isError: true,
                });

                logger.error(error, {
                    [LOGS_KEY]: 'Error while enriching a lazy personalized component',
                    componentProps: JSON.stringify(this.props),
                });
            }
        }

        render() {
            const { isLazy, appName, perseusName, component } = this.props;
            const { isLoading, componentData = {}, isError } = this.state;
            const { contextEnrichment = {}, items } = componentData;

            if (isError) {
                return null;
            }

            if (isLazy && isEmpty(items) && (!appName || !perseusName || !component.name)) {
                logger.error('Could not fetch lazy personalized component - missing required params', {
                    [LOGS_KEY]: 'Error while enriching a lazy personalized component',
                    app_name: appName,
                    perseus_name: perseusName,
                    component_name: component.name,
                });

                return null;
            }

            const props = {
                ...this.props,
                ...contextEnrichment,
                isLoading,
                component: componentData,
            };

            return <WrappedComponent {...props} />;
        }
    }

    SelfFetchingComponent.propTypes = {
        component: object.isRequired,
        sectionName: string,
        isLazy: bool,
        isLoading: bool,
        recentlyViewedChoiceGigs: arrayOf(number),
        abTests: object,
        choiceAlgos: objectOf(arrayOf(string)),
        appName: string,
        perseusName: string,
    };

    SelfFetchingComponent.defaultProps = {
        isLoading: false,
        isLazy: false,
    };

    return SelfFetchingComponent;
};
